import React, { useState } from "react";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const contact = siteConfig.contact || {};

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setShowSuccess(false);

    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.firstName.trim()) nextErrors.firstName = "Enter your first name.";
    if (!formData.lastName.trim()) nextErrors.lastName = "Enter your last name.";
    if (!formData.email.trim()) {
      nextErrors.email = "Enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formData.subject) nextErrors.subject = "Choose a subject.";
    if (!formData.message.trim()) {
      nextErrors.message = "Tell us how we can help.";
    } else if (formData.message.trim().length < 10) {
      nextErrors.message = "Please include at least 10 characters.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      const firstInvalidField = document.getElementById(Object.keys(nextErrors)[0]);
      firstInvalidField?.focus();
      return;
    }

    setErrors({});
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 650));
    setIsSubmitting(false);
    setShowSuccess(true);
  };

  const fieldA11y = (name) => ({
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `${name}-error` : undefined,
  });

  return (
    <main className="contact-page">
      <header className="contact-hero">
        <p className="contact-kicker">CUSTOMER SERVICE / GENERAL INQUIRIES</p>
        <h1>Let’s talk.</h1>
        <p>
          Questions about fit, an order or the way something is made? Send a
          note and the {siteConfig.brandName} team will get back to you.
        </p>
      </header>

      <div className="contact-layout">
        <aside className="contact-details" aria-label="Contact information">
          <p className="contact-kicker">DIRECT LINE</p>
          <div className="contact-details__item"><span>Company</span><p>{siteConfig.companyName}</p></div>
          {contact.email && (
            <div className="contact-details__item">
              <span>Email</span>
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </div>
          )}
          {contact.phone && (
            <div className="contact-details__item">
              <span>Phone</span>
              <a href={`tel:${contact.phone.replace(/[^+\d]/g, "")}`}>
                {contact.phone}
              </a>
            </div>
          )}
          {contact.address && (
            <div className="contact-details__item">
              <span>Company address</span>
              <address>{contact.address}</address>
            </div>
          )}
          {contact.hours && (
            <div className="contact-details__item">
              <span>Hours</span>
              <p>{contact.hours}</p>
            </div>
          )}
          <p className="contact-details__note">
            We usually reply within two business days. Order questions move
            fastest when you include your order number.
          </p>
        </aside>

        <section className="contact-form-panel" aria-labelledby="contact-form-title">
          <div className="contact-form-panel__heading">
            <span>FORM 01</span>
            <h2 id="contact-form-title">Send an inquiry</h2>
            <p>Required fields are marked with an asterisk.</p>
          </div>

          {showSuccess && (
            <div className="contact-success" role="status" aria-live="polite">
              <strong>Your email draft is ready.</strong>
              <a href={`mailto:${contact.email}?subject=${encodeURIComponent(`suddlecorner inquiry: ${formData.subject}`)}&body=${encodeURIComponent(`${formData.firstName} ${formData.lastName}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\n${formData.message}`)}`}>Open your email app and send the inquiry</a>
            </div>
          )}

          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="contact-field">
              <label htmlFor="firstName">First name *</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleInputChange}
                {...fieldA11y("firstName")}
              />
              {errors.firstName && <span id="firstName-error">{errors.firstName}</span>}
            </div>

            <div className="contact-field">
              <label htmlFor="lastName">Last name *</label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleInputChange}
                {...fieldA11y("lastName")}
              />
              {errors.lastName && <span id="lastName-error">{errors.lastName}</span>}
            </div>

            <div className="contact-field">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleInputChange}
                {...fieldA11y("email")}
              />
              {errors.email && <span id="email-error">{errors.email}</span>}
            </div>

            <div className="contact-field">
              <label htmlFor="phone">Phone (optional)</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>

            <div className="contact-field contact-field--wide">
              <label htmlFor="subject">Subject *</label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                {...fieldA11y("subject")}
              >
                <option value="">Select one</option>
                <option value="order">Order support</option>
                <option value="product">Product or fit question</option>
                <option value="returns">Return or exchange</option>
                <option value="stockist">Wholesale or stockist inquiry</option>
                <option value="other">Something else</option>
              </select>
              {errors.subject && <span id="subject-error">{errors.subject}</span>}
            </div>

            <div className="contact-field contact-field--wide">
              <label htmlFor="message">Message *</label>
              <textarea
                id="message"
                name="message"
                rows="7"
                value={formData.message}
                onChange={handleInputChange}
                {...fieldA11y("message")}
              />
              {errors.message && <span id="message-error">{errors.message}</span>}
            </div>

            <button className="contact-submit" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Preparing…" : "Prepare email"}
            </button>
          </form>
        </section>
      </div>
    </main>
  );
}

export default Contact;
